import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, getPaginated, post, patch, del } from '@/lib/api'

import type { EmployeeStatus } from '@company/shared'


// ==================== Types ====================

export type { EmployeeStatus }
export type CompanyRole = 'director' | 'finance' | 'hr' | 'admin' | 'ops' | 'pm' | 'developer'

export interface Employee {
  id: string
  employeeNo: string
  name: string
  email: string
  phone: string | null
  avatar: string | null
  role: CompanyRole
  departmentId: string | null
  positionId: string | null
  managerId: string | null
  status: EmployeeStatus
  hireDate: string
  resignDate: string | null
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface EmployeeWithDetails extends Employee {
  department?: {
    id: string
    name: string
    code: string
  } | null
  position?: {
    id: string
    name: string
    code: string
    level: number | null
  } | null
  manager?: {
    id: string
    name: string
  } | null
}

export interface ActiveEmployee {
  id: string
  name: string
  email: string
  employeeNo?: string
}

export interface EmployeeListParams {
  departmentId?: string
  positionId?: string
  status?: EmployeeStatus
  role?: CompanyRole
  search?: string
  page?: number
  pageSize?: number
}

export interface CreateEmployeeInput {
  name: string
  email: string
  phone?: string
  avatar?: string
  role?: CompanyRole
  departmentId?: string
  positionId?: string
  managerId?: string
  hireDate: string
}

export interface UpdateEmployeeInput {
  name?: string
  email?: string
  phone?: string
  avatar?: string
  role?: CompanyRole
  departmentId?: string
  positionId?: string
  managerId?: string
  hireDate?: string
  resignDate?: string
}

// ==================== Query Keys ====================

export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (params: EmployeeListParams) => [...employeeKeys.lists(), params] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeKeys.details(), id] as const,
  active: () => [...employeeKeys.all, 'active'] as const,
  byIds: (ids: string[]) => [...employeeKeys.all, 'byIds', ids] as const,
}

// ==================== Hooks ====================

/**
 * 获取员工列表
 */
export function useEmployees(params: EmployeeListParams = {}) {
  return useQuery({
    queryKey: employeeKeys.list(params),
    queryFn: () => getPaginated<Employee>('/hr/employees', params as Record<string, unknown>),
  })
}

/**
 * 获取单个员工详情
 */
export function useEmployee(id: string) {
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => get<EmployeeWithDetails>(`/hr/employees/${id}`),
    enabled: !!id,
  })
}

/**
 * 获取活跃员工列表（下拉选择用）
 */
export function useActiveEmployees() {
  return useQuery({
    queryKey: employeeKeys.active(),
    queryFn: () => get<ActiveEmployee[]>('/hr/employees/active'),
  })
}

/**
 * 根据 ID 数组获取员工信息
 * 利用 active employees 缓存，过滤出指定 ID 的员工
 */
export function useEmployeesByIds(ids: (string | null | undefined)[]) {
  const validIds = ids.filter((id): id is string => !!id)
  const { data: activeEmployees, isLoading, error } = useActiveEmployees()

  // 创建 ID -> 员工映射
  const employeeMap = new Map<string, ActiveEmployee>()
  if (activeEmployees) {
    for (const emp of activeEmployees) {
      employeeMap.set(emp.id, emp)
    }
  }

  // 过滤出请求的员工
  const employees = validIds
    .map((id) => employeeMap.get(id))
    .filter((emp): emp is ActiveEmployee => !!emp)

  return {
    data: employees,
    employeeMap,
    isLoading,
    error,
    getEmployeeName: (id: string | null | undefined): string => {
      if (!id) {return '-'}
      const emp = employeeMap.get(id)
      return emp?.name ?? '-'
    },
  }
}

/**
 * 创建员工
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateEmployeeInput) => post<Employee>('/hr/employees', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.active() })
    },
  })
}

/**
 * 更新员工
 */
export function useUpdateEmployee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEmployeeInput }) =>
      patch<Employee>(`/hr/employees/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.active() })
    },
  })
}

/**
 * 删除员工
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/hr/employees/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.active() })
    },
  })
}

/**
 * 禁用员工账号
 */
export function useDisableEmployee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Employee>(`/hr/employees/${id}/disable`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
    },
  })
}

/**
 * 启用员工账号
 */
export function useEnableEmployee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Employee>(`/hr/employees/${id}/enable`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
    },
  })
}

// ==================== Avatar ====================

export interface AvatarResponse {
  id: string
  originalName: string
  mimeType: string
  size: number
  createdAt: string
}

export interface AvatarUrlResponse {
  url: string
  expiresAt: string
}

/**
 * 上传员工头像
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }): Promise<AvatarResponse> => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`/api/v1/hr/employees/${id}/avatar`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (!response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorData = (await response.json()) as any
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
        throw new Error(errorData.error?.message || '上传失败')
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = (await response.json()) as any
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
      return result.data
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['employee-avatar', id] })
    },
  })
}

/**
 * 获取员工头像 URL
 */
export function useEmployeeAvatar(id: string) {
  return useQuery({
    queryKey: ['employee-avatar', id],
    queryFn: () => get<AvatarUrlResponse | null>(`/hr/employees/${id}/avatar`),
    enabled: !!id,
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}
